import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MainLayout from './shared/layouts/MainLayout';
import AuthLayout from './shared/layouts/AuthLayout';
import Login from './modules/base/pages/Login';
import Dashboard from './modules/base/pages/Dashboard';
import ProductsList from './modules/products/pages/ProductsList';
import ProductForm from './modules/products/pages/ProductForm';
import ProductCatalogPrint from './modules/products/pages/ProductCatalogPrint';
import ProductsByCategory from './modules/products/pages/ProductsByCategory';
import CategoriesManagement from './modules/products/pages/CategoriesManagement';
import BarcodeLabelPrint from './modules/products/pages/BarcodeLabelPrint';
import TenantManagement from './modules/saas/pages/TenantManagement';
import InventoryDashboard from './modules/inventory/pages/InventoryDashboard';
import DamageReporting from './modules/inventory/pages/DamageReporting';
import SalesOrdersList from './modules/sales/pages/SalesOrdersList';
import SalesHoldsList from './modules/sales/pages/SalesHoldsList';
import HoldForm from './modules/sales/pages/HoldForm';
import SalesReturnsList from './modules/sales/pages/SalesReturnsList';
import SalesReturnForm from './modules/sales/pages/SalesReturnForm';
import SalesOrderForm from './modules/sales/pages/SalesOrderForm';
import SalesOrderPrint from './modules/sales/pages/SalesOrderPrint';
import SalesOrdersPrint from './modules/sales/pages/SalesOrdersPrint';
import CustomersList from './modules/sales/pages/CustomersList';
import CustomerForm from './modules/sales/pages/CustomerForm';
import CustomerDetails from './modules/sales/pages/CustomerDetails';
import QuotationsList from './modules/quotations/pages/QuotationsList';
import QuotationForm from './modules/quotations/pages/QuotationForm';
import QuotationDetail from './modules/quotations/pages/QuotationDetail';
import PurchaseOrdersList from './modules/purchase/pages/PurchaseOrdersList';
import PurchaseOrderForm from './modules/purchase/pages/PurchaseOrderForm';
import PurchaseReturnsList from './modules/purchase/pages/PurchaseReturnsList';
import PurchaseReturnForm from './modules/purchase/pages/PurchaseReturnForm';
import SuppliersList from './modules/purchase/pages/SuppliersList';
import SupplierDetails from './modules/purchase/pages/SupplierDetails';
import Reports from './modules/reporting/pages/Reports';
import ReportCategoryView from './modules/reporting/pages/ReportCategoryView';
import ReportView from './modules/reporting/pages/ReportView';
import Expenses from './modules/finance/pages/Expenses';
import BankAccountsList from './modules/finance/pages/BankAccountsList';
import BankAccountForm from './modules/finance/pages/BankAccountForm';
import VouchersList from './modules/finance/pages/VouchersList';
import VoucherForm from './modules/finance/pages/VoucherForm';
import ChequebooksList from './modules/finance/pages/ChequebooksList';
import ChequebookDetail from './modules/finance/pages/ChequebookDetail';
import ChequebookForm from './modules/finance/pages/ChequebookForm';
import ChequesList from './modules/finance/pages/ChequesList';
import ChequeForm from './modules/finance/pages/ChequeForm';
import ChequePrint from './modules/finance/pages/ChequePrint';
import VanTripsList from './modules/van-sales/pages/VanTripsList';
import VansList from './modules/van-sales/pages/VansList';
import VanTripDetail from './modules/van-sales/pages/VanTripDetail';
import VanReconciliation from './modules/van-sales/pages/VanReconciliation';
import Settings from './modules/settings/pages/Settings';
import EmailTemplateForm from './modules/settings/pages/EmailTemplateForm';
import PrintTemplatePreview from './modules/settings/pages/PrintTemplatePreview';
import WarrantyPeriods from './modules/settings/pages/WarrantyPeriods';
import RentalList from './modules/rentals/pages/RentalList';
import RentalForm from './modules/rentals/pages/RentalForm';
import RentalReturnForm from './modules/rentals/pages/RentalReturnForm';
import RentalPrintBill from './modules/rentals/pages/RentalPrintBill';

import Profile from './modules/base/pages/Profile';
import UsersList from './modules/base/pages/UsersList';
import RolesList from './modules/base/pages/RolesList';
import DatabaseBackup from './modules/base/pages/DatabaseBackup';
import { selectIsAuthenticated } from './store/slices/authSlice';
import { ProtectedRoute } from './shared/components/PermissionGate';

function App() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector((state) => state.auth.user);

  // Auto-redirect Super Admin to their management dashboard
  if (isAuthenticated && user?.isSuperAdmin && (window.location.pathname === '/dashboard' || window.location.pathname === '/')) {
    return <Navigate to="/super-admin" replace />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <AuthLayout>
              <Login />
            </AuthLayout>
          )
        }
      />
      <Route
        path="/*"
        element={
          isAuthenticated ? (
            <MainLayout />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      >
        <Route path="dashboard" element={<ProtectedRoute module="dashboard"><Dashboard /></ProtectedRoute>} />
        <Route path="products" element={<ProtectedRoute permission="products.products.read"><ProductsList /></ProtectedRoute>} />
        <Route path="products/catalog/print" element={<ProtectedRoute permission="products.products.read"><ProductCatalogPrint /></ProtectedRoute>} />
        <Route path="products/category-tree" element={<ProtectedRoute permission="products.products.read"><ProductsByCategory /></ProtectedRoute>} />
        <Route path="products/barcode/print" element={<ProtectedRoute permission="products.products.read"><BarcodeLabelPrint /></ProtectedRoute>} />
        <Route path="products/categories" element={<ProtectedRoute permission="products.categories.read"><CategoriesManagement /></ProtectedRoute>} />
        <Route path="products/new" element={<ProtectedRoute permission="products.products.create"><ProductForm /></ProtectedRoute>} />
        <Route path="products/:id" element={<ProtectedRoute permission="products.products.read"><ProductForm /></ProtectedRoute>} />
        <Route path="inventory" element={<ProtectedRoute module="inventory"><InventoryDashboard /></ProtectedRoute>} />
        <Route path="inventory/damages" element={<ProtectedRoute module="inventory"><DamageReporting /></ProtectedRoute>} />
        <Route path="sales" element={<ProtectedRoute permission="sales.orders.read"><SalesOrdersList /></ProtectedRoute>} />
        <Route path="sales/holds" element={<ProtectedRoute permission="sales.orders.read"><SalesHoldsList /></ProtectedRoute>} />
        <Route path="sales/holds/new" element={<ProtectedRoute permission="sales.orders.create"><HoldForm /></ProtectedRoute>} />
        <Route path="sales/holds/:id" element={<ProtectedRoute permission="sales.orders.read"><HoldForm /></ProtectedRoute>} />
        <Route path="sales/returns" element={<ProtectedRoute permission="sales.returns.read"><SalesReturnsList /></ProtectedRoute>} />
        <Route path="sales/returns/new" element={<ProtectedRoute permission="sales.returns.create"><SalesReturnForm /></ProtectedRoute>} />
        <Route path="sales/returns/:id" element={<ProtectedRoute permission="sales.returns.read"><SalesReturnForm /></ProtectedRoute>} />
        <Route path="quotations" element={<ProtectedRoute permission="quotations.quotations.read"><QuotationsList /></ProtectedRoute>} />
        <Route path="quotations/new" element={<ProtectedRoute permission="quotations.quotations.create"><QuotationForm /></ProtectedRoute>} />
        <Route path="quotations/:id" element={<ProtectedRoute permission="quotations.quotations.read"><QuotationDetail /></ProtectedRoute>} />
        <Route path="quotations/:id/edit" element={<ProtectedRoute permission="quotations.quotations.update"><QuotationForm /></ProtectedRoute>} />
        <Route path="sales/orders/new" element={<ProtectedRoute permission="sales.orders.create"><SalesOrderForm /></ProtectedRoute>} />
        <Route path="sales/orders/:id" element={<ProtectedRoute permission="sales.orders.read"><SalesOrderForm /></ProtectedRoute>} />
        <Route path="sales/orders/:id/print" element={<ProtectedRoute permission="sales.orders.read"><SalesOrderPrint /></ProtectedRoute>} />
        <Route path="sales/print" element={<ProtectedRoute permission="sales.orders.read"><SalesOrdersPrint /></ProtectedRoute>} />
        <Route path="purchases" element={<ProtectedRoute permission="purchases.orders.read"><PurchaseOrdersList /></ProtectedRoute>} />
        <Route path="purchases/returns" element={<ProtectedRoute permission="purchases.returns.read"><PurchaseReturnsList /></ProtectedRoute>} />
        <Route path="purchases/returns/new" element={<ProtectedRoute permission="purchases.returns.create"><PurchaseReturnForm /></ProtectedRoute>} />
        <Route path="purchases/returns/:id" element={<ProtectedRoute permission="purchases.returns.read"><PurchaseReturnForm /></ProtectedRoute>} />
        <Route path="purchases/suppliers" element={<ProtectedRoute permission="purchases.suppliers.read"><SuppliersList /></ProtectedRoute>} />
        <Route path="purchases/suppliers/:id" element={<ProtectedRoute permission="purchases.suppliers.read"><SupplierDetails /></ProtectedRoute>} />
        <Route path="purchases/orders/new" element={<ProtectedRoute permission="purchases.orders.create"><PurchaseOrderForm /></ProtectedRoute>} />
        <Route path="purchases/orders/:id" element={<ProtectedRoute permission="purchases.orders.read"><PurchaseOrderForm /></ProtectedRoute>} />
        <Route path="customers" element={<ProtectedRoute permission="sales.customers.read"><CustomersList /></ProtectedRoute>} />
        <Route path="customers/new" element={<ProtectedRoute permission="sales.customers.create"><CustomerForm /></ProtectedRoute>} />
        <Route path="customers/:id" element={<ProtectedRoute permission="sales.customers.read"><CustomerForm /></ProtectedRoute>} />
        <Route path="customers/:id/details" element={<ProtectedRoute permission="sales.customers.read"><CustomerDetails /></ProtectedRoute>} />
        <Route path="users" element={<ProtectedRoute permission="users.users.read"><UsersList /></ProtectedRoute>} />
        <Route path="roles" element={<ProtectedRoute permission="roles.roles.read"><RolesList /></ProtectedRoute>} />
        <Route path="reports" element={<ProtectedRoute module="reports"><Reports /></ProtectedRoute>} />
        <Route path="reports/category/:categoryId" element={<ProtectedRoute module="reports"><ReportCategoryView /></ProtectedRoute>} />
        <Route path="reports/view/:reportId" element={<ProtectedRoute module="reports"><ReportView /></ProtectedRoute>} />
        <Route path="rentals" element={<ProtectedRoute><RentalList /></ProtectedRoute>} />
        <Route path="rentals/new" element={<ProtectedRoute><RentalForm /></ProtectedRoute>} />
        <Route path="rentals/view/:id" element={<ProtectedRoute><RentalForm /></ProtectedRoute>} />
        <Route path="rentals/:id/return" element={<ProtectedRoute><RentalReturnForm /></ProtectedRoute>} />
        <Route path="rentals/:id/print" element={<ProtectedRoute><RentalPrintBill /></ProtectedRoute>} />
        <Route path="van-sales" element={<ProtectedRoute module="van-sales"><VanTripsList /></ProtectedRoute>} />
        <Route path="van-sales/vans" element={<ProtectedRoute permission="van-sales.vans.read"><VansList /></ProtectedRoute>} />
        <Route path="van-sales/trips/new" element={<ProtectedRoute permission="van-sales.trips.create"><VanTripDetail /></ProtectedRoute>} />
        <Route path="van-sales/trips/:id" element={<ProtectedRoute permission="van-sales.trips.read"><VanTripDetail /></ProtectedRoute>} />
        <Route path="van-sales/trips/:id/reconciliation" element={<ProtectedRoute permission="van-sales.trips.read"><VanReconciliation /></ProtectedRoute>} />
        <Route path="expenses" element={<ProtectedRoute module="expenses"><Expenses /></ProtectedRoute>} />
        <Route path="finance/bank-accounts" element={<ProtectedRoute module="expenses"><BankAccountsList /></ProtectedRoute>} />
        <Route path="finance/bank-accounts/new" element={<ProtectedRoute module="expenses"><BankAccountForm /></ProtectedRoute>} />
        <Route path="finance/bank-accounts/:id" element={<ProtectedRoute module="expenses"><BankAccountForm /></ProtectedRoute>} />
        <Route path="finance/vouchers" element={<ProtectedRoute module="expenses"><VouchersList /></ProtectedRoute>} />
        <Route path="finance/vouchers/new" element={<ProtectedRoute module="expenses"><VoucherForm /></ProtectedRoute>} />
        <Route path="finance/vouchers/:id" element={<ProtectedRoute module="expenses"><VoucherForm /></ProtectedRoute>} />
        <Route path="finance/vouchers/:id/edit" element={<ProtectedRoute module="expenses"><VoucherForm /></ProtectedRoute>} />
        <Route path="finance/chequebooks" element={<ProtectedRoute module="expenses"><ChequebooksList /></ProtectedRoute>} />
        <Route path="finance/chequebooks/new" element={<ProtectedRoute module="expenses"><ChequebookForm /></ProtectedRoute>} />
        <Route path="finance/chequebooks/:id/edit" element={<ProtectedRoute module="expenses"><ChequebookForm /></ProtectedRoute>} />
        <Route path="finance/chequebooks/:id" element={<ProtectedRoute module="expenses"><ChequebookDetail /></ProtectedRoute>} />
        <Route path="finance/cheques" element={<ProtectedRoute module="expenses"><ChequesList /></ProtectedRoute>} />
        <Route path="finance/cheques/new" element={<ProtectedRoute module="expenses"><ChequeForm /></ProtectedRoute>} />
        <Route path="finance/cheques/:id/print" element={<ProtectedRoute module="expenses"><ChequePrint /></ProtectedRoute>} />
        <Route path="finance/cheques/:id" element={<ProtectedRoute module="expenses"><ChequeForm /></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute module="settings"><Settings /></ProtectedRoute>} />
        <Route path="products/warranty-periods" element={<ProtectedRoute module="products"><WarrantyPeriods /></ProtectedRoute>} />
        <Route path="settings/email-templates/new" element={<ProtectedRoute module="settings"><EmailTemplateForm /></ProtectedRoute>} />
        <Route path="settings/email-templates/edit/:id" element={<ProtectedRoute module="settings"><EmailTemplateForm /></ProtectedRoute>} />
        <Route
          path="settings/print-templates/preview/:templateId"
          element={
            <ProtectedRoute module="settings">
              <PrintTemplatePreview />
            </ProtectedRoute>
          }
        />
        <Route path="settings/backup" element={<ProtectedRoute module="settings"><DatabaseBackup /></ProtectedRoute>} />
        <Route path="profile" element={<Profile />} />
        <Route path="super-admin" element={<ProtectedRoute checkSuperAdmin><TenantManagement /></ProtectedRoute>} />
        <Route path="" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

export default App;
